import jwtDecode from 'jwt-decode';
import update from 'immutability-helper';
import _ from 'lodash';
import {
  AUTH_ATTACH_USER_ID,
  AUTH_LOGIN,
  AUTH_LOGOUT,
  AUTH_SESSION_EXPIRED,
  AUTH_UPDATE_USER,
  INITIAL_STATE,
  AUTH_UPDATE_TOKEN,
  AUTH_SET_REGION,
  AUTH_UPDATE_TOKEN_DETAILS,
  AUTH_UPDATE_AUTH_CONTEXT,
  AUTH_ADD_RESOURCE_TO_USER,
  AUTH_REMOVE_RESOURCE_FROM_USER,
  AUTH_CLEAR_SIGN_OUT_CODE,
  AUTH_SET_START_WITH_BIOMETRIC,
  AUTH_SET_SUGGEST_BIOMETRIC_ENROLLMENT,
} from '../actions';
import { createReducer } from '../../utils/redux/reducers.utils';

const initialState = {
  token: null,
  accessToken: null,
  refreshToken: null,
  tokenDetails: {},
  refreshTokenDetails: {},
  user: {},
  id: '',
  region: null,
  signOutCode: null,
  startWithBiometric: false,
  suggestBiometricEnrollment: false,
};

const authLogin = (state, { token, accessToken, refreshToken, user }) =>
  update(state, {
    $merge: {
      token,
      accessToken,
      tokenDetails: { ...jwtDecode(accessToken) },
      refreshToken,
      refreshTokenDetails: { ...jwtDecode(refreshToken) },
      user: update(state.user, { $set: user }),
      signOutCode: null,
    },
  });

const authUpdateToken = (state, { accessToken, refreshToken }) => {
  const payload = {
    accessToken,
    tokenDetails: { ...jwtDecode(accessToken) },
  };

  if (!_.isUndefined(refreshToken)) {
    payload.refreshToken = refreshToken;
    payload.refreshTokenDetails = { ...jwtDecode(refreshToken) };
  }

  return update(state, {
    $merge: payload,
  });
};

const updateAuthContext = (state, { context }) => {
  const payload = {
    tokenDetails: context,
  };

  return update(state, {
    $merge: payload,
  });
};

const updateTokenDetails = (state, { accessToken }) =>
  update(state, {
    $merge: {
      accessToken,
      tokenDetails: { ...jwtDecode(accessToken) },
    },
  });

const attachUserId = (state, { id }) =>
  update(state, {
    id: { $set: id },
  });

const loadInitialState = (state, { auth = {} }) =>
  update(state, {
    $merge: auth,
  });

const updateUserDetails = (state, { user }) =>
  update(state, {
    $merge: {
      user: update(state.user, { $merge: user }),
    },
  });

const addResourceToAuthUser = (state, { resource }) =>
  update(state, {
    user: {
      professionalProfile: {
        resources: { $push: [resource] },
      },
    },
  });

const removeResourceFromAuthUser = (state, { resourceId }) =>
  update(state, {
    user: {
      professionalProfile: {
        resources: { $apply: resources => resources.filter(r => r.id !== resourceId) },
      },
    },
  });

const setRegion = (state, { region }) =>
  update(state, {
    region: { $set: region },
  });

const authLogout = (state, { signOutCode } = {}) => {
  return {
    ...initialState,
    user: {
      email: state.user?.email || '',
      type: state.user?.type,
    },
    signOutCode: signOutCode || null,
  };
};

// Soft logout: only clears session tokens. Preserves auth identity (id, user,
// region) so BiometricUnlockScreen can request a challenge with the existing
// userId + organizationId. Other slices (organization, knowledge, ...) don't
// subscribe to this action, so their state stays intact across the unlock.
const authSessionExpired = (state, { signOutCode } = {}) =>
  update(state, {
    $merge: {
      token: null,
      accessToken: null,
      refreshToken: null,
      tokenDetails: {},
      refreshTokenDetails: {},
      startWithBiometric: true,
      signOutCode: signOutCode || null,
    },
  });

const clearSignOutCode = state =>
  update(state, {
    signOutCode: { $set: null },
  });

const setStartWithBiometric = (state, payload) =>
  update(state, {
    startWithBiometric: { $set: payload },
  });

const setSuggestBiometricEnrollment = (state, payload) =>
  update(state, {
    suggestBiometricEnrollment: { $set: payload },
  });

const actionsRepo = {
  [INITIAL_STATE]: loadInitialState,
  [AUTH_LOGIN]: authLogin,
  [AUTH_UPDATE_TOKEN]: authUpdateToken,
  [AUTH_UPDATE_AUTH_CONTEXT]: updateAuthContext,
  [AUTH_ATTACH_USER_ID]: attachUserId,
  [AUTH_LOGOUT]: authLogout,
  [AUTH_SESSION_EXPIRED]: authSessionExpired,
  [AUTH_UPDATE_USER]: updateUserDetails,
  [AUTH_ADD_RESOURCE_TO_USER]: addResourceToAuthUser,
  [AUTH_REMOVE_RESOURCE_FROM_USER]: removeResourceFromAuthUser,
  [AUTH_SET_REGION]: setRegion,
  [AUTH_UPDATE_TOKEN_DETAILS]: updateTokenDetails,
  [AUTH_CLEAR_SIGN_OUT_CODE]: clearSignOutCode,
  [AUTH_SET_START_WITH_BIOMETRIC]: setStartWithBiometric,
  [AUTH_SET_SUGGEST_BIOMETRIC_ENROLLMENT]: setSuggestBiometricEnrollment,
};

export default createReducer({ initialState, actionsRepo });
